import {payloadPublisher, ExperiencePerformanceTypes, TenantType} from "@atlassian/ufo";
import {Config} from "@atlaskit/react-ufo/config";
import { init as initReactUFO } from "@atlaskit/react-ufo/interaction-metrics-init";
import { configure as configureResourceTimings } from "@atlaskit/react-ufo/resource-timing";
import traceUFOPageLoad from "@atlaskit/react-ufo/trace-pageload";

import { analyticsClient } from '../analytics';
import { version } from "react";
import { resourceTimingsConfig } from "./utils/get-resource-config";
import getCurrentRouteName from "./utils/get-current-route-name";
import { getCustomData } from "./utils/get-custom-data";
import { InteractionMetrics } from "@atlaskit/react-ufo/interaction-metrics";
import { ReportedTimings } from "@atlaskit/react-ufo/ssr";
import {
  type SSRConfig,
  configure as configureSSR
} from "@atlaskit/react-ufo/ssr";
import imkt from "../imkt";

declare global {
  interface Window {
      SSR_detailMetrics?: ReportedTimings;
      SSR_totalMetrics?: Record<string, number>;
      SSR_region?: string;
  }
}

let isInitialized = false;


function getAdditionalFields(interaction: InteractionMetrics) {
  try {
    return { custom: getCustomData(interaction) };
  } catch (error) {
    return {};
  }
}

export const getSSRTotal = (): number => {
  try {
      return (window.SSR_totalMetrics || {})["duration"] || 0;
  } catch (e) {
      console.warn("Error getting SSR total", e);
      return 0;
  }
};

export const getSSRTimings = (): ReportedTimings => {
  try {
      return window.SSR_detailMetrics || {};
  } catch (e) {
      console.warn("Error getting SSR timings", e);
      return {};
  }
};

// SSR Configuration
export const ssrConfig: SSRConfig = {
  getDoneMark: getSSRTotal,
  getTimings: getSSRTimings,
  getFeatureFlags: () => null
};

const getFCPStartTime = (): number => {
  try {
      const paintEntries = window.performance.getEntriesByType("paint");
      const firstContentfulPaint = paintEntries.find(
          (entry) => entry.name === "first-contentful-paint"
      );
      return firstContentfulPaint?.startTime || 0;
  } catch (e) {
      console.warn("Error getting first contentful paint", e);
      return 0;
  }
};

export async function initUFO(): Promise<void> {
  const globalAnalyticsClient = analyticsClient();
  payloadPublisher.setup({
    featureFlagClient: { getValue: () => true },
    product: "marketing",
    gasv3: globalAnalyticsClient,
    app: {
      version: { web: "1.0.0" },
      framework: { name: "react", version }
    },
    tenantType: TenantType.RealUser,
    resourceTimings: {
      mapResources: (url) => url,
      sanitiseEndpoints: (url) => url
    },
    performanceConfig: {
      histogram: {
        [ExperiencePerformanceTypes.PageLoad]: {
          initial: {
            fmp: "1000_2000_3000_4000",
            tti: "1000_2000_3000_4000"
          },
          transition: {
            fmp: "1000_2000",
            tti: "1000_2000"
          }
        },
        [ExperiencePerformanceTypes.PageSegmentLoad]: {
          initial: {
            fmp: "1000_2000",
            tti: "1000_2000"
          },
          transition: {
            fmp: "1000_2000",
            tti: "1000_2000"
          }
        },
        [ExperiencePerformanceTypes.InlineResult]: {
          result: "1000_2000",
          response: "1000_2000"
        },
        [ExperiencePerformanceTypes.Custom]: {
          duration: "1000_2000"
        }
      }
    }
  });
  if (!isInitialized && globalAnalyticsClient) {
    configureResourceTimings(resourceTimingsConfig);

    const config: Config = {
      ssr: { getSSRDoneTime: getFCPStartTime },
      product: 'marketing',
      region: 'unknown',
      enableCompleteUsingDoubleRAF: true,
      enableBetterPageVisibilityApi: true,
      additionalPayloadData: getAdditionalFields,
      kind: {
        page_load: 1,
        transition: 1,
        hover: 0,
        legacy: 1,
        typing: 0,
        press: 1,
      },
      capability: {
        feature_flag_access: 100,
        react_profiler: 1
      },
      namePrefix: "magnolia.",
      segmentPrefix: "magnolia.",
      typingMethod: "timeout",
      removeInteractionsUFOPrefixes: true,
      removePageSegmentsUFOPrefixes: false,
      vc: {
        enabled: true,
        heatmapSize: 200,
        oldDomUpdates: false,
        devToolsEnabled: true,
        selectorConfig: {
            id: false,
            role: true,
            className: true,
            testId: true
        }
      }
    }

    configureResourceTimings(resourceTimingsConfig);
    configureSSR(ssrConfig);
    initReactUFO(Promise.resolve(analyticsClient()), config);
    // @ts-ignore
    const status = window.performance.getEntriesByType('navigation')[0]?.responseStatus;
    const perf_portal_experiences = [
      'end-bsm-now',
      'software/statuspage',
      'software/jira/service-management/free',
      'company/careers/all-jobs',
      'software/confluence/set-knowledge-free',
      'company/careers',
      'software/compass',
      'legal/privacy-policy',
      'software/jira/service-management/powered-by',
      'company/contact',
      'software/rovo'
    ]
    if (status === 200) {
      let ufoRouteName = 'all';
      const enableExperiencePerRoute = await imkt.utils.featureGate.checkGate("gwp-enable-magnolia-experience-per-route");
      
      // for omlet, author, proof, truth (with /wac/ prefix)
      let route = window.location.pathname.replace(/^\/wac(\/)?/, '');
      
      if(enableExperiencePerRoute){  
        // for globaledge
        if (route.startsWith('/')) {
          route = route.replace('/', '');
        }
        // only update route name for routes we want seperate experiences for in perfportal
        if(perf_portal_experiences.includes(route)){
          ufoRouteName = getCurrentRouteName(route);
        }
      }

      traceUFOPageLoad(ufoRouteName, route || "/");
    }

  }
  isInitialized = true;
}

if (document.readyState !== "loading") {
  initUFO();
} else {
  window.addEventListener("DOMContentLoaded", () => initUFO);
}
